import { DayCalendarFilterOptionType, DdiziType } from '@kinderlabs-pos/shared-data-type';
import { getGroupBy } from '@kinderlabs-pos/shared-util';
import {
	DdiziQueryState,
	DdiziState,
	PosElectronStoreState,
	PrintReceiptState,
	StoreInfoState,
} from '@kinderlabs-pos/state';
import { SelectableListItem } from '@kinderlabs-pos/ui-atoms';
import {
	Box,
	CircularProgress,
	List,
	ListItem,
	ListItemButton,
	ListSubheader,
	Stack,
	Typography,
} from '@mui/material';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { useAtom, useAtomValue } from 'jotai';
import { Fragment, useEffect, useMemo } from 'react';
import { DdiziManagementPage } from '.';
import { DdiziDisplay } from '../../components/DdiziDisplay';
import { TicketPrintButton } from '../../modules/tickets/components/buttons/TicketPrintButton';
import { DdiziEnterExitButtons } from './DdiziEnterExitButtons';
import { DdiziFilterType } from './DdiziFilterDialog';
import { DdiziTimeAdjustButtons } from './DdiziTimeAdjustButtons';
import { MappingTicketApis } from '../../modules/mappingTicket/mappingTicketApis';
import { TicketMappingInfoType } from '../../modules/mappingTicket/mappingTicketType';

export const DdiziListContent = ({
	filter,
	keyword,
	option,
}: {
	keyword?: string;
	filter: DdiziFilterType;
	option: DayCalendarFilterOptionType;
}) => {
	const storeInfo = useAtomValue(StoreInfoState.curStoreInfo);
	const { enterpriseId } = useAtomValue(StoreInfoState.curStoreAndDevice);
	const ticketShareStoreIdList = storeInfo.policy.ticketShareStoreIdList || [];
	const storeIdListToSearch = useMemo(
		() => [...ticketShareStoreIdList, storeInfo.id],
		[ticketShareStoreIdList, storeInfo.id]
	);
	const curDeviceInfo = StoreInfoState.useCurDeviceInfoForOrder();
	const 띠지프린터라벨출력 = PrintReceiptState.LabelPrinter.usePrintLabelPrinter({
		labelPrinterList: curDeviceInfo.labelPrinterList,
	});

	const [selectedDdizi, selectDdizi] = useAtom(DdiziManagementPage.selectedDdizi);
	const localSettings = PosElectronStoreState.usePosMainPageLocalSettings().data;
	const {
		data: ddiziDataPage,
		isFetchingNextPage,
		fetchNextPage,
		hasNextPage,
	} = useInfiniteQuery({
		...DdiziQueryState.keys.infinite({
			params: {
				storeIdList: storeIdListToSearch,
				search: keyword,
				status: filter.status,
				posId: filter.targetPosId,
				...option,
			},
		}),
		getNextPageParam: (lastPage, allPage) => {
			return lastPage.startCursorId ? { startCursorId: lastPage.startCursorId } : undefined;
		},
		refetchOnWindowFocus: true,
	});

	const ddiziData = useMemo(() => {
		if (!ddiziDataPage) return [];
		else return ddiziDataPage.pages.flatMap((data) => data.content);
	}, [ddiziDataPage]);

	const ddizisGroupBy = getGroupBy(ddiziData, (item) => item.publishInfo.orderId);

	const exitedDdiziIdsToday = useMemo(
		() =>
			ddiziData
				.filter(
					(ddizi) => ddizi.status === 'EXITED' && dayjs(ddizi.publishInfo.published).isToday()
				)
				.map((ddizi) => ddizi.id),
		[ddiziData]
	);
	const { data: recentHistoryByDdiziId } = useQuery({
		...DdiziQueryState.keys.recentHistoryBulk({ ddiziIds: exitedDdiziIdsToday }),
	});

	const { print영수증띠지Async } = PrintReceiptState.useReceiptDdiziPrinter({
		storeId: storeInfo.id,
		deviceType: 'POS',
	});

	const handlePrinterClick = (ddizi: DdiziType) => async () => {
		const ddiziData = { ...DdiziState.utils.getDdizi인쇄Data(ddizi) };
		const designOptions = PrintReceiptState.LabelPrinter.getDesignOptions({
			localSettings,
			deviceNum: ddiziData.deviceNum,
		});

		// 기존 띠지 출력에 영향이 가지 않도록 갭 라벨지일 때만 고객 정보를 조회한다.
		let 바코드총데이터: TicketMappingInfoType | undefined;
		try {
			if (enterpriseId === 'vaunce' && designOptions.useGapLabel)
				바코드총데이터 = await MappingTicketApis.getTicketMappingInfo({ ticketId: ddizi.id });
		} catch {
			// 넘어가
		}

		const 권종 = ddiziData.message.labelName.includes('키즈')
			? '키즈'
			: ddiziData.message.labelName.includes('보호자')
			? '보호자'
			: '일반';

		await 띠지프린터라벨출력({
			...ddiziData,
			meta: 바코드총데이터
				? {
						고객명: 바코드총데이터.mappingMember?.name,
						생년월일: 바코드총데이터.mappingMember?.birth,
						성별: 바코드총데이터.mappingMember?.gender,
						권종,
				  }
				: undefined,
			deviceType: 'POS',
			...designOptions,
			storeName: storeInfo.name,
			modelName:
				curDeviceInfo.labelPrinterList.find((p) => p.logicalName === ddiziData.deviceNum)
					?.modelName ?? '',
		});
		await print영수증띠지Async({
			ddizi,
		});
	};

	useEffect(() => {
		if (selectedDdizi) {
			selectDdizi(ddiziData.find((ddizi) => ddizi.id === selectedDdizi.id));
		}
	}, [selectedDdizi, ddiziData]);

	return (
		<List>
			{[...Object.keys(ddizisGroupBy)]
				.sort((a: string, b: string) => b.localeCompare(a))
				.map((key, idx) => (
					<Fragment key={idx}>
						<ListSubheader
							sx={(theme) => ({
								py: 1,
								borderBottom: `1px solid ${theme.palette.divider}`,
							})}>
							<Typography variant={'body1'}>{`주문번호 : ${key}`}</Typography>
						</ListSubheader>
						{ddizisGroupBy[key].map((ddizi, ddiziIdx) => {
							return (
								<SelectableListItem
									key={ddiziIdx}
									selected={ddizi.id === selectedDdizi?.id}
									onClick={() => {
										selectDdizi(ddizi);
									}}
									sx={(theme) => ({
										py: 1,
										borderBottom: `1px solid ${theme.palette.divider}`,
									})}>
									<DdiziDisplay
										ddizi={ddizi}
										sx={{ width: '100%' }}
										userOnPage={'TICKET'}
										itemButton={
											dayjs(ddizi.publishInfo.published).isToday() && (
												<Stack>
													<Stack
														direction={'row'}
														spacing={1}>
														<TicketPrintButton
															label={'재발권'}
															onClick={handlePrinterClick(ddizi)}
														/>
														<DdiziEnterExitButtons
															ddizi={ddizi}
															recentHistory={recentHistoryByDdiziId?.[ddizi.id]}
														/>
													</Stack>
													<Stack
														direction={'row'}
														spacing={1}>
														<Box sx={{ visibility: 'hidden' }}>
															<TicketPrintButton
																label={'재발권'}
																onClick={handlePrinterClick(ddizi)}
															/>
														</Box>
														<DdiziTimeAdjustButtons ddizi={ddizi} />
													</Stack>
												</Stack>
											)
										}
									/>
								</SelectableListItem>
							);
						})}
					</Fragment>
				))}
			{isFetchingNextPage && (
				<ListItem>
					<Stack
						width={'100%'}
						alignItems={'center'}>
						<CircularProgress />
					</Stack>
				</ListItem>
			)}
			{hasNextPage && !isFetchingNextPage && (
				<ListItemButton onClick={() => fetchNextPage()}>
					<Stack
						width={'100%'}
						alignItems={'center'}>
						더 보기
					</Stack>
				</ListItemButton>
			)}
		</List>
	);
};
